import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const languageOptions = ['English + Hindi', 'Hindi', 'English', 'Bengali', 'Punjabi', 'Urdu', 'Marathi', 'Bhojpuri'];
const socialSharePlatforms = [
  { key: 'Instagram', label: 'Instagram', icon: 'IG', url: 'https://www.instagram.com/', color: 'from-[#F97316] to-[#C026D3]' },
  { key: 'Facebook', label: 'Facebook', icon: 'f', url: 'https://www.facebook.com/', color: 'from-[#2563EB] to-[#1D4ED8]' },
  { key: 'YouTube', label: 'YouTube', icon: 'YT', url: 'https://studio.youtube.com/', color: 'from-[#EF4444] to-[#B91C1C]' },
  { key: 'X', label: 'X', icon: 'X', url: 'https://x.com/compose/post', color: 'from-slate-950 to-slate-700' },
  { key: 'WhatsApp', label: 'WhatsApp', icon: 'WA', url: 'https://web.whatsapp.com/', color: 'from-[#16A34A] to-[#047857]' },
];

const cleanResultText = (text = '') => String(text || '')
  .replace(/^#{1,6}\s*/gm, '')
  .replace(/\*\*/g, '')
  .trim();

const extractResultSection = (text = '', heading = '') => {
  const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = String(text || '').match(new RegExp(`${escaped}:\\s*([\\s\\S]*?)(?=\\n(?:Instagram|Facebook|YouTube|X|WhatsApp):|$)`, 'i'));
  return (match?.[1] || '').trim();
};

const isSocialPostDraft = (approval = {}) => {
  const text = `${approval.title || ''}\n${approval.body || ''}`.toLowerCase();
  return ['instagram:', 'facebook:', 'youtube:', 'whatsapp:', 'social post', 'post pack'].some((term) => text.includes(term));
};

const socialCardText = (text = '', platformKey = '') => extractResultSection(text, platformKey) || 'No separate copy found for this platform yet. Edit the draft text or ask AI Manager to regenerate a Social Post Pack.';

const copyTextToClipboard = async (text = '') => {
  await navigator.clipboard.writeText(String(text || ''));
};

const shareSocialText = async (platform, text = '') => {
  const cleanText = String(text || '').trim();
  if (navigator.share && cleanText) {
    await navigator.share({
      title: `KalaManch ${platform.label} post`,
      text: cleanText,
    });
    return;
  }
  await copyTextToClipboard(cleanText);
  window.open(platform.key === 'X' ? `https://x.com/compose/post?text=${encodeURIComponent(cleanText.slice(0, 260))}` : platform.url, '_blank', 'noopener,noreferrer');
};

const formatDate = (value) => {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const AiManager = () => {
  const { artist, isAuthenticated } = useArtistAuth();
  const [managerData, setManagerData] = useState(null);
  const [reports, setReports] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [chatReply, setChatReply] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    preferred_language: 'English + Hindi',
    content_quota: 2,
  });
  const [editedApprovals, setEditedApprovals] = useState({});

  const pendingCount = useMemo(() => approvals.filter((item) => item.status === 'pending').length, [approvals]);

  const fetchManager = async () => {
    setError('');
    try {
      const [statusRes, reportsRes, approvalsRes] = await Promise.all([
        axios.get(`${API}/v1/manager`, { withCredentials: true }),
        axios.get(`${API}/v1/manager/reports?limit=6`, { withCredentials: true }),
        axios.get(`${API}/v1/manager/approvals?status=all`, { withCredentials: true }),
      ]);
      setManagerData(statusRes.data);
      setReports(reportsRes.data?.reports || []);
      setApprovals(approvalsRes.data?.approvals || []);
      setSettings({
        preferred_language: statusRes.data?.manager?.preferred_language || 'English + Hindi',
        content_quota: statusRes.data?.manager?.content_quota || 2,
      });
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Login as an artist to use AI Manager.');
      } else {
        setError(err.response?.data?.detail || 'AI Manager could not load right now.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManager();
  }, []);

  const updateSettings = async () => {
    setSaving(true);
    setNotice('');
    setError('');
    try {
      const res = await axios.patch(`${API}/v1/manager/settings`, settings, { withCredentials: true });
      setManagerData((current) => ({ ...(current || {}), manager: res.data?.manager }));
      setNotice('AI Manager settings saved.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Settings could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const setManagerStatus = async (status) => {
    setSaving(true);
    setNotice('');
    setError('');
    try {
      const endpoint = status === 'paused' ? 'pause' : 'resume';
      const res = await axios.post(`${API}/v1/manager/${endpoint}`, {}, { withCredentials: true });
      setManagerData((current) => ({ ...(current || {}), manager: res.data?.manager }));
      setNotice(status === 'paused' ? 'AI Manager paused.' : 'AI Manager resumed.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Manager status could not be updated.');
    } finally {
      setSaving(false);
    }
  };

  const sendChat = async () => {
    if (!message.trim()) return;
    setChatLoading(true);
    setNotice('');
    setError('');
    setChatReply('');
    try {
      const res = await axios.post(`${API}/v1/manager/chat`, { message }, { withCredentials: true });
      setChatReply(cleanResultText(res.data?.reply || ''));
      if (res.data?.approval) {
        setApprovals((current) => [res.data.approval, ...current]);
        setNotice('AI Manager created a draft approval item for you.');
      }
      setMessage('');
      fetchManager();
    } catch (err) {
      setError(err.response?.data?.detail || 'AI Manager chat failed.');
    } finally {
      setChatLoading(false);
    }
  };

  const decideApproval = async (approvalId, action) => {
    setSaving(true);
    setNotice('');
    setError('');
    try {
      const body = action === 'approve'
        ? { edited_body: editedApprovals[approvalId] || '' }
        : { reason: 'Rejected by artist' };
      const res = await axios.post(`${API}/v1/manager/approvals/${approvalId}/${action}`, body, { withCredentials: true });
      setApprovals((current) => current.map((item) => (item.id === approvalId ? res.data.approval : item)));
      setNotice(action === 'approve' ? 'Draft approved.' : 'Draft rejected.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Approval could not be updated.');
    } finally {
      setSaving(false);
    }
  };

  const loginArtist = () => {
    window.dispatchEvent(new Event('openArtistJoin'));
  };

  return (
    <main className="min-h-screen bg-[#F6F8FB] pt-28 sm:pt-32">
      <SEO
        title="KalaManch AI Manager | Artist Growth Assistant"
        description="AI Manager for KalaManch artists to manage drafts, reports, approvals, and growth guidance."
        keywords="KalaManch AI Manager, artist manager AI, artist growth assistant, artist approvals, KalaManch AI Studio"
      />

      <section className="px-5 pb-16 pt-6 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.9fr_1.4fr]">
          <aside className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg">
            <p className="text-xs font-black uppercase tracking-[0.35em] text-[#FF6F00]">Artist Manager</p>
            <h1 className="mt-3 text-4xl font-black leading-tight text-slate-950 md:text-5xl">
              KalaManch AI Manager
            </h1>
            <p className="mt-4 text-lg leading-8 text-slate-700">
              A manager inbox for artist growth: chat, drafts, approvals, weekly reports, and AI Studio tool orchestration.
            </p>

            {!isAuthenticated && (
              <div className="mt-6 rounded-2xl border border-orange-200 bg-orange-50 p-4">
                <p className="font-bold text-slate-900">Login as an artist to activate AI Manager.</p>
                <button type="button" onClick={loginArtist} className="btn-primary mt-4">Artist Login</button>
              </div>
            )}

            {managerData?.manager && (
              <div className="mt-6 space-y-3">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.25em] text-slate-500">Status</p>
                  <p className="mt-1 text-2xl font-black capitalize text-slate-950">{managerData.manager.status}</p>
                </div>
                <div className="rounded-2xl bg-green-50 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.25em] text-green-700">Free AI Meter</p>
                  <p className="mt-1 font-bold text-slate-950">
                    {managerData.usage?.daily_remaining ?? 0} today / {managerData.usage?.monthly_remaining ?? 0} monthly left
                  </p>
                </div>
                <div className="rounded-2xl bg-purple-50 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.25em] text-purple-700">Pending</p>
                  <p className="mt-1 text-2xl font-black text-slate-950">{pendingCount} approval(s)</p>
                </div>
              </div>
            )}
          </aside>

          <section className="space-y-6">
            {loading ? (
              <div className="rounded-[2rem] bg-white p-8 text-center font-bold text-slate-700 shadow">Loading AI Manager...</div>
            ) : error ? (
              <div className="rounded-[2rem] border border-red-100 bg-red-50 p-6 font-bold text-red-700">{error}</div>
            ) : null}

            {notice && <div className="rounded-2xl border border-green-100 bg-green-50 p-4 font-bold text-green-800">{notice}</div>}

            {managerData?.manager && (
              <>
                <div className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.28em] text-[#4A148C]">Controls</p>
                      <h2 className="mt-2 text-2xl font-black text-slate-950">Manager settings</h2>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={saving || managerData.manager.status === 'paused'}
                        onClick={() => setManagerStatus('paused')}
                        className="btn-outline"
                      >
                        Pause
                      </button>
                      <button
                        type="button"
                        disabled={saving || managerData.manager.status === 'active'}
                        onClick={() => setManagerStatus('active')}
                        className="btn-primary"
                      >
                        Resume
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3 md:grid-cols-3">
                    <select
                      value={settings.preferred_language}
                      onChange={(event) => setSettings((current) => ({ ...current, preferred_language: event.target.value }))}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold"
                    >
                      {languageOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    <input
                      type="number"
                      min="1"
                      max="7"
                      value={settings.content_quota}
                      onChange={(event) => setSettings((current) => ({ ...current, content_quota: Number(event.target.value) }))}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold"
                      aria-label="Weekly content quota"
                    />
                    <button type="button" disabled={saving} onClick={updateSettings} className="btn-primary">Save Settings</button>
                  </div>
                  <p className="mt-3 text-sm text-slate-500">Next planned cycle: {formatDate(managerData.manager.next_run_at)}</p>
                </div>

                <div className="rounded-[2rem] border border-purple-100 bg-white p-6 shadow-lg">
                  <p className="text-xs font-black uppercase tracking-[0.28em] text-[#4A148C]">Chat</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">Ask your AI Manager</h2>
                  <textarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    className="mt-4 min-h-36 w-full rounded-2xl border border-slate-200 bg-white p-4 text-slate-900"
                    placeholder="Ask for a weekly plan, profile audit, social post draft, pitch, or next growth steps."
                  />
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button type="button" disabled={chatLoading} onClick={sendChat} className="btn-primary">
                      {chatLoading ? 'Thinking...' : 'Ask Manager'}
                    </button>
                    <Link to="/ai-studio" className="btn-outline">Open AI Studio</Link>
                  </div>
                  {chatReply && (
                    <div className="mt-5 whitespace-pre-wrap rounded-2xl bg-slate-50 p-4 leading-7 text-slate-800">
                      {chatReply}
                    </div>
                  )}
                </div>

                <div className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg">
                  <p className="text-xs font-black uppercase tracking-[0.28em] text-[#FF6F00]">Approvals</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">Drafts needing your OK</h2>
                  <div className="mt-5 space-y-4">
                    {approvals.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center font-bold text-slate-500">
                        No AI Manager drafts yet.
                      </div>
                    ) : approvals.map((approval) => {
                      const draftText = editedApprovals[approval.id] ?? approval.edited_body ?? approval.body;
                      const socialDraft = isSocialPostDraft(approval);
                      return (
                        <article key={approval.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">{approval.kind}</p>
                              <h3 className="mt-1 text-lg font-black text-slate-950">{approval.title}</h3>
                            </div>
                            <span className="rounded-full bg-white px-3 py-1 text-xs font-black capitalize text-[#4A148C]">{approval.status}</span>
                          </div>

                          {socialDraft && (
                            <div className="mt-4 rounded-3xl border border-orange-100 bg-white p-4">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                  <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Ready to share</p>
                                  <h4 className="mt-1 text-xl font-black text-slate-950">Social post cards</h4>
                                  <p className="mt-1 text-sm font-semibold text-slate-600">Approve the draft first, then copy/share manually.</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    copyTextToClipboard(draftText);
                                    setNotice('All social post draft text copied.');
                                  }}
                                  className="w-fit rounded-full bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700 transition hover:bg-emerald-100"
                                >
                                  Copy all
                                </button>
                              </div>
                              <div className="mt-4 grid gap-4 xl:grid-cols-2">
                                {socialSharePlatforms.map((platform) => {
                                  const sectionText = socialCardText(draftText, platform.key);
                                  const shareHref = platform.key === 'X'
                                    ? `https://x.com/compose/post?text=${encodeURIComponent(sectionText.slice(0, 260))}`
                                    : platform.url;
                                  return (
                                    <article key={platform.key} className="rounded-3xl border border-slate-100 bg-slate-50 p-4 shadow-sm">
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex min-w-0 items-center gap-3">
                                          <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${platform.color} text-sm font-black text-white shadow`}>
                                            {platform.icon}
                                          </span>
                                          <div className="min-w-0">
                                            <h5 className="text-base font-black text-slate-950">{platform.label}</h5>
                                            <p className="text-xs font-bold text-slate-500">AI Manager draft</p>
                                          </div>
                                        </div>
                                        <a href={shareHref} target="_blank" rel="noreferrer" className="shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[#FF6F00] shadow-sm">
                                          Open
                                        </a>
                                      </div>
                                      <div className="mt-4 max-h-52 overflow-y-auto rounded-2xl border border-white bg-white p-3">
                                        <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{sectionText}</p>
                                      </div>
                                      <div className="mt-4 grid grid-cols-2 gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            copyTextToClipboard(sectionText);
                                            setNotice(`${platform.label} draft copied.`);
                                          }}
                                          className="rounded-full border border-orange-200 px-4 py-2 text-xs font-bold text-[#FF6F00] hover:bg-orange-50"
                                        >
                                          Copy
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => shareSocialText(platform, sectionText)}
                                          className="rounded-full bg-[#FF6F00] px-4 py-2 text-xs font-bold text-white hover:bg-[#E65100]"
                                        >
                                          Share
                                        </button>
                                      </div>
                                    </article>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <textarea
                            value={draftText}
                            onChange={(event) => setEditedApprovals((current) => ({ ...current, [approval.id]: event.target.value }))}
                            className="mt-4 min-h-40 w-full rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-800"
                            disabled={approval.status !== 'pending'}
                          />
                          {approval.status === 'pending' && (
                            <div className="mt-3 flex flex-wrap gap-3">
                              <button type="button" disabled={saving} onClick={() => decideApproval(approval.id, 'approve')} className="btn-primary">Approve</button>
                              <button type="button" disabled={saving} onClick={() => decideApproval(approval.id, 'reject')} className="btn-outline">Reject</button>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </div>

                <div className="grid gap-6 xl:grid-cols-2">
                  <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-lg">
                    <p className="text-xs font-black uppercase tracking-[0.28em] text-slate-500">Reports</p>
                    <h2 className="mt-2 text-2xl font-black text-slate-950">Recent manager reports</h2>
                    <div className="mt-4 space-y-3">
                      {reports.length === 0 ? (
                        <p className="rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-slate-500">Weekly reports will appear here after manager cycles are enabled.</p>
                      ) : reports.map((report) => (
                        <div key={report.id} className="rounded-2xl bg-slate-50 p-4">
                          <p className="text-sm font-black text-slate-950">{report.summary}</p>
                          <p className="mt-2 text-xs font-semibold text-slate-500">{formatDate(report.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-lg">
                    <p className="text-xs font-black uppercase tracking-[0.28em] text-slate-500">Tool Registry</p>
                    <h2 className="mt-2 text-2xl font-black text-slate-950">Existing AI Studio tools wrapped</h2>
                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      {(managerData.tools || []).map((tool) => (
                        <div key={tool.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                          <p className="font-black text-slate-900">{tool.title}</p>
                          <p className="text-xs font-bold text-slate-500">{tool.category}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </section>
    </main>
  );
};

export default AiManager;
